import {
  useAppRename,
  useAppDescriptionUpdate,
  useUserActiveOrganization
} from "@/api";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyMuted } from "@/components/typography/Muted";
import { TypographySM } from "@/components/typography/SM";
import { Button } from "@/components/ui/button";
import { CommandSnippet } from "@/components/ui/commandSnippet";
import { InfoBox } from "@/components/ui/infoBox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { getFormData } from "@/lib/js-helpers/getFormData";
import { ExternalLink, Terminal } from "lucide-react";
import { Dispatch, FormEvent, SetStateAction, useState } from "react";
import { PartialApp } from "../types/app.type";

interface Props {
  app: PartialApp;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function AppEdit({ app, setOpen }: Props) {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [appRename] = useAppRename();
  const [appDescriptionUpdate] = useAppDescriptionUpdate();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const { activeOrganization } = useUserActiveOrganization();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!activeOrganization) return;

    setLoadingSubmit(true);

    const { name, description } = getFormData(e.currentTarget, [
      "name",
      "description"
    ] as const);

    if (name !== app.displayName)
      await appRename({
        variables: {
          organizationSlug: activeOrganization.slug,
          appID: app.id,
          appName: name as string
        }
      });

    if (description !== app.description)
      await appDescriptionUpdate({
        variables: {
          organizationSlug: activeOrganization.slug,
          appID: app.id,
          appDescription: description as string
        }
      });

    setLoadingSubmit(false);
    setOpen(false);
  }

  function checkSubmitValidity(e: FormEvent<HTMLFormElement>) {
    const { name, description } = getFormData(e.currentTarget, [
      "name",
      "description"
    ] as const);

    const nameIsValid = !!name && name !== app.displayName;
    const descriptionIsValid = description !== app.description;

    setDisableSubmit(!name || (!nameIsValid && !descriptionIsValid));
  }

  return (
    <Sheet open onOpenChange={setOpen}>
      <SheetContent
        className="flex flex-col gap-y-2 overflow-y-auto"
        style={{ width: "clamp(400px, 45vw, 800px)" }}
      >
        <SheetHeader>Edit app</SheetHeader>
        <SheetDescription>
          Edit the content of {app.displayName}
        </SheetDescription>
        <form onSubmit={handleSubmit} onChange={checkSubmitValidity}>
          <Label htmlFor="name">Name *</Label>
          <Input
            name="name"
            required
            defaultValue={app.displayName}
            className="mb-2"
          />

          <Label htmlFor="description">Description</Label>
          <Textarea name="description" defaultValue={app.description} />
          <div className="mt-6 gap-4 flex justify-end items-center">
            {loadingSubmit && <LoadingCircle />}
            <Button type="submit" disabled={disableSubmit}>
              Save changes
            </Button>
          </div>
        </form>

        <Separator className="mt-2 mb-4" />

        <TypographyBase className="font-medium">
          Want to edit the app code?
        </TypographyBase>

        <TypographySM className="text-brand-gray-800 dark:text-brand-gray-100">
          To edit the app code, run the following command in your terminal:
        </TypographySM>
        <div>
          <CommandSnippet copyable>
            {`numerous download -o ${activeOrganization?.slug} -a ${app.slug}`}
          </CommandSnippet>
        </div>
        <TypographyMuted className="mb-10">
          Only the developer of the currently deployed version of this app can
          download the code.
        </TypographyMuted>

        <InfoBox>
          <TypographySM className="font-semibold text-brand-primary">
            <Terminal className="inline mr-1" size={18} />
            New to the Numerous CLI?
          </TypographySM>
          <TypographySM className="text-brand-gray-800 dark:text-brand-gray-100 mt-4">
            If you are not yet set up with the Numerous CLI, run the following
            command in your terminal:
          </TypographySM>
          <CommandSnippet className="my-4" copyable>
            pip install numerous
          </CommandSnippet>
          <TypographySM className="text-brand-gray-800 dark:text-brand-gray-100 mb-2">
            After the Numerous CLI is installed, run the following command in
            your terminal to log in:
          </TypographySM>
          <CommandSnippet copyable>numerous login</CommandSnippet>

          <TypographySM className="text-brand-gray-800 dark:text-brand-gray-300 mt-8">
            For more help getting started, check out the{" "}
            <a
              href="https://www.numerous.com/docs/cli#install"
              className="hover:underline underline-offset-4 font-medium text-brand-gray-950 dark:text-brand-primary"
              target="_blank"
              rel="noreferrer"
            >
              installation guide on our docs page
              <ExternalLink size={14} className="inline ml-1 mb-0.5" />
            </a>
          </TypographySM>
        </InfoBox>
      </SheetContent>
    </Sheet>
  );
}

export default AppEdit;
